import { createContext, useContext, useEffect, useCallback, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentUserProfile } from './AuthContext.api';
import { ACCOUNT_CONFIG_QUERY_KEY } from '@/api/accountConfig';
import { HANDOVER_BLOCK_SETTINGS_QUERY_KEY, HANDOVER_TEMPLATES_QUERY_KEY } from '@/api/handoverTemplates';
import { SHIFT_FILL_REQUESTS_QUERY_KEY } from '@/api/shiftFill';
import { logoutKhova } from '@/api/auth';
import { clearSession, markKhovaSession, waitForRefresh } from '@/api/authSession';
import { normalizeUserRole, type UserProfile } from '@/api/user';
import { legacyAdapter } from '@/integrations/legacyAdapter';
import { getAssignedStores, type Store } from '@/api/stores';
import { getDetectedTimezone } from '@/lib/timezones';
import {
  applyColorToDocument,
  cacheAccountColor,
  clearCachedAccountColor,
  hasCachedAccountColor,
  hexToHsl,
  resetColorOnDocument,
} from '@/lib/colors';
import { useAccountBranding } from '@/hooks/useAccountBranding';
import { identifyUser, resetPosthogIdentity } from '@/analytics/posthog.identity';
import { identifySentryUser, resetSentryIdentity } from '@/analytics/errorReporting';
import { AnalyticsEventName } from '@/analytics/analytics.events';
import { track } from '@/analytics/track';
import { changeLanguage } from '@/i18n';
import { SUPPORTED_LOCALES, type Locale } from '@/i18n/locales';
import { resetTranslationScheduler } from '@/pages/newsfeed/TranslationScheduler';
import { PENDING_CONFIRMATIONS_QUERY_KEY, POST_CONFIRMATIONS_QUERY_KEY } from '@/pages/newsfeed/MustReads.keys';
import { doNothing } from '@/utils/doNothing';

// Fail-safe: release the render if the account-color fetch stalls past this.
const COLOR_READY_TIMEOUT_MS = 4000;

// Everything cached for the previous identity that must not leak into the next one.
const SESSION_QUERY_KEYS = [
  ['user'],
  legacyAdapter.queryKeys.accountUser,
  ACCOUNT_CONFIG_QUERY_KEY,
  ['learning'],
  ['stores'],
  ['store-events'],
  ['store-events-access'],
  ['handover'],
  ['handover-recipients'],
  SHIFT_FILL_REQUESTS_QUERY_KEY,
  ['store-employees'],
  ['user-phone-number'],
  PENDING_CONFIRMATIONS_QUERY_KEY,
  POST_CONFIRMATIONS_QUERY_KEY,
  HANDOVER_BLOCK_SETTINGS_QUERY_KEY,
  HANDOVER_TEMPLATES_QUERY_KEY,
];

interface AuthContextType {
  user: UserProfile | null;
  isSessionLoading: boolean;
  isColorReady: boolean;
  logout: () => Promise<void>;
  setUser: (user: UserProfile) => void;
  // The stores the user is assigned to, and which one is "current" - shared
  // app-wide so switching stores (e.g. on Home) is reflected everywhere that
  // needs "the current store", not just where the picker lives. Defaults to
  // the first assigned store once they load.
  stores: Store[];
  // True until the signed-in user's stores have loaded: an empty `stores` then means "not yet", not "none".
  isStoresLoading: boolean;
  selectedStoreId: string | null;
  setSelectedStoreId: (storeId: string) => void;
  // The selected store's timezone, falling back to the device's while loading
  // or when the user has no assigned store - "today" for store-scoped logic
  // keyed on the *selected* store (e.g. the Home date header) should be
  // computed against this. A specific task's own store timezone is a
  // different thing - see resolveTaskTimezone in pages/tasks.
  storeTimezone: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const rememberSession = (user: UserProfile | null): void => {
  if (user) {
    markKhovaSession();
  }
};

const syncLanguageFromUser = (user: UserProfile): void => {
  const slug = user.languageSlug;
  if (!slug || !(SUPPORTED_LOCALES as readonly string[]).includes(slug)) {
    return;
  }
  changeLanguage(slug as Locale);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [hasCachedColor] = useState(hasCachedAccountColor);
  const [hasColorTimedOut, setHasColorTimedOut] = useState(false);

  const { data: user = null, isPending: isSessionLoading } = useQuery({
    queryKey: ['user'],
    queryFn: async ({ signal }) => {
      const profile = await getCurrentUserProfile(signal);
      rememberSession(profile);
      identifyUser(profile);
      identifySentryUser(profile);
      return profile;
    },
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (user?.languageSlug) {
      syncLanguageFromUser(user);
    }
  }, [user?.languageSlug]);

  const { branding, isFetched: isBrandingFetched } = useAccountBranding(user);

  const { data: stores = [], isPending: isStoresPending } = useQuery({
    queryKey: ['stores'],
    queryFn: getAssignedStores,
    enabled: !!user,
    staleTime: Infinity,
  });

  // The explicit choice from the picker; falls back to the first assigned store once stores load,
  // and again if that choice stops being valid (e.g. unassigned from it). Derived during render,
  // not corrected after the fact in an effect, so there is no extra render where it is briefly wrong.
  const [explicitStoreId, setExplicitStoreId] = useState<string | null>(null);
  const selectedStoreId =
    explicitStoreId && stores.some((s) => s.id === explicitStoreId) ? explicitStoreId : (stores[0]?.id ?? null);
  const selectedStore = stores.find((s) => s.id === selectedStoreId) ?? null;
  // A disabled query (no user yet) also reports pending: only a signed-in user's stores are ever "loading".
  const isStoresLoading = Boolean(user) && isStoresPending;
  const storeTimezone = selectedStore?.timezone ?? getDetectedTimezone();

  // The colour comes from the account's Khova branding; a signed-out visitor has nothing to wait for.
  const isSignedOut = !isSessionLoading && !user;
  const isColorSettled = hasCachedColor || isBrandingFetched || isSignedOut;
  const isColorReady = isColorSettled || hasColorTimedOut;

  useEffect(() => {
    if (isColorSettled) {
      return;
    }
    const timeoutId = window.setTimeout(() => setHasColorTimedOut(true), COLOR_READY_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isColorSettled]);

  useEffect(() => {
    if (!branding) {
      return;
    }
    const hsl = branding.primaryColor ? hexToHsl(branding.primaryColor) : null;
    if (hsl) {
      applyColorToDocument(hsl);
      cacheAccountColor(hsl);
    } else {
      resetColorOnDocument();
      clearCachedAccountColor();
    }
  }, [branding]);

  const setUser = useCallback(
    (profile: UserProfile) => {
      const user: UserProfile = { ...profile, role: normalizeUserRole(profile.role) };
      queryClient.setQueryData(['user'], user);
      queryClient.resetQueries({ queryKey: legacyAdapter.queryKeys.accountUser }).catch(doNothing);
      queryClient.resetQueries({ queryKey: ACCOUNT_CONFIG_QUERY_KEY }).catch(doNothing);
      rememberSession(user);
      syncLanguageFromUser(user);
      identifyUser(user);
      identifySentryUser(user);
    },
    [queryClient],
  );

  const clearLocalSession = useCallback(() => {
    legacyAdapter.session.clearToken();
    resetPosthogIdentity();
    resetSentryIdentity();
    resetColorOnDocument();
    clearCachedAccountColor();
    resetTranslationScheduler();
    SESSION_QUERY_KEYS.forEach((queryKey) => queryClient.removeQueries({ queryKey }));
    // Not a query-cache entry, so SESSION_QUERY_KEYS above does not clear it: the next person to
    // sign in on this device must not inherit the previous person's store choice.
    setExplicitStoreId(null);
    changeLanguage();
  }, [queryClient]);

  const goToWelcome = useCallback(() => navigate('/welcome', { replace: true }), [navigate]);

  const logout = async () => {
    // A refresh already in flight rotates the tokens: let it land first so the revoke targets
    // the live refresh token (and, on web, the server cannot set fresh cookies after logout).
    // `clearSession` also bumps the session generation, so a later refresh answer is dropped.
    await waitForRefresh();
    // The server-side revoke is best effort: tokens and local state go regardless, so a
    // failing backend can never keep the user signed in on this device.
    const isRevoked = await logoutKhova()
      .then(() => true)
      .catch(() => false);
    track(AnalyticsEventName.USER_SIGNED_OUT, { revoke_failed: !isRevoked });
    await clearSession();
    clearLocalSession();
    goToWelcome();
  };

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isSessionLoading,
        isColorReady,
        setUser,
        logout,
        stores,
        isStoresLoading,
        selectedStoreId,
        setSelectedStoreId: setExplicitStoreId,
        storeTimezone,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
